import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaCreditCard,
  FaExchangeAlt,
  FaMobileAlt,
  FaUniversity,
  FaWallet,
} from 'react-icons/fa';
import InfrastructureLeasingBuyerPostSaleMilestones from '../../components/infrastructureLeasing/InfrastructureLeasingBuyerPostSaleMilestones';
import InfrastructureLeasingBuyerPostSaleTabs from '../../components/infrastructureLeasing/InfrastructureLeasingBuyerPostSaleTabs';
import {
  fetchBuyerPostSaleSnapshot,
  postBuyerInfrastructurePaymentComplete,
} from '../../services/infrastructureLeasingListing.service';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/infrastructure_leasing_buyer_post_sale.scss';

const PAYMENT_METHODS = [
  {
    id: 'upi',
    label: 'UPI',
    hint: 'Google Pay, PhonePe, Paytm, BHIM…',
    Icon: FaMobileAlt,
  },
  {
    id: 'card',
    label: 'Credit / Debit card',
    hint: 'Visa, Mastercard, RuPay — corporate cards supported',
    Icon: FaCreditCard,
  },
  {
    id: 'netbanking',
    label: 'Net banking',
    hint: 'All major banks',
    Icon: FaUniversity,
  },
  {
    id: 'wallet',
    label: 'Wallets',
    hint: 'Paytm Wallet and others',
    Icon: FaWallet,
  },
  {
    id: 'bank_transfer',
    label: 'Bank transfer',
    hint: 'NEFT / RTGS (reference will appear on invoice)',
    Icon: FaExchangeAlt,
  },
];

function formatDue(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export default function InfrastructureLeasingBuyerPaymentPage() {
  const { listingId: listingParam, enquiryId: enquiryParam } = useParams();
  const navigate = useNavigate();
  const listingIdNum = useMemo(() => {
    const n = parseInt(String(listingParam), 10);
    return Number.isFinite(n) && n >= 1 ? n : NaN;
  }, [listingParam]);
  const enquiryIdNum = useMemo(() => {
    const n = parseInt(String(enquiryParam), 10);
    return Number.isFinite(n) && n >= 1 ? n : NaN;
  }, [enquiryParam]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  /** Selected gateway-style option before confirming */
  const [selectedMethod, setSelectedMethod] = useState('');
  /** After user clicks Complete payment, show method picker */
  const [showMethodPicker, setShowMethodPicker] = useState(false);

  const basePath = `/infrastructure-leasing/my-enquiries/${listingIdNum}/${enquiryIdNum}`;

  const load = useCallback(async () => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchBuyerPostSaleSnapshot(listingIdNum, enquiryIdNum);
      if (res?.success && res.data) {
        setSnapshot(res.data);
        const st = String(res.data.pipelineStage || '').toLowerCase();
        if (st === 'invoice_issued') {
          navigate(`${basePath}/invoice`, { replace: true });
          return;
        }
        if (st === 'lease_active') {
          navigate(`${basePath}/lease-tracking`, { replace: true });
          return;
        }
      } else {
        setSnapshot(null);
        toast.error(res?.message || 'Could not load payment details.');
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.message;
      if (status === 409) {
        toast.warning(msg || 'Complete agreement signing first.');
        navigate(`${basePath}/agreement`, { replace: true });
      } else {
        toast.error(msg || 'Could not load payment details.');
      }
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [listingIdNum, enquiryIdNum, navigate, basePath]);

  useEffect(() => {
    void load();
  }, [load]);

  const handlePay = async () => {
    if (!selectedMethod) {
      toast.warning('Choose how you want to pay.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await postBuyerInfrastructurePaymentComplete(listingIdNum, enquiryIdNum, {
        paymentMethod: selectedMethod,
      });
      if (res?.success) {
        toast.success(res.message || 'Payment recorded.');
        navigate(`${basePath}/invoice`, { replace: true });
      } else {
        toast.error(res?.message || 'Payment could not be completed.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Payment failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) {
    return (
      <div className="container-fluid ile-buyer-post-sale py-4">
        <p className="text-muted">Invalid link.</p>
        <Link to="/infrastructure-leasing/my-enquiries">Back to My enquiries</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-fluid ile-buyer-post-sale py-4">
        <div className="text-muted py-5">Loading payment…</div>
      </div>
    );
  }

  const stage = String(snapshot?.pipelineStage || '').toLowerCase();
  const showPay = stage === 'payment_due';

  return (
    <div className="container-fluid ile-buyer-post-sale py-4">
      <div className="ile-buyer-post-sale__shell">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/dashboard/buyer">Buyer dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/infrastructure-leasing/my-enquiries">My enquiries</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Payment
            </li>
          </ol>
        </nav>

        <InfrastructureLeasingBuyerPostSaleTabs basePath={basePath} />

      <h1 className="h4 mb-2">Lease payment</h1>
      <p className="text-muted small mb-4">
        {snapshot?.listingTitle ? (
          <>
            <span className="fw-semibold text-body">{snapshot.listingTitle}</span>
            {snapshot?.agreementReference ? (
              <span className="text-muted"> · {snapshot.agreementReference}</span>
            ) : null}
          </>
        ) : (
          'Infrastructure leasing'
        )}
      </p>

      {snapshot?.milestones?.length ? (
        <InfrastructureLeasingBuyerPostSaleMilestones milestones={snapshot.milestones} />
      ) : null}

      <div className="card border shadow-sm">
        <div className="card-header bg-white fw-semibold py-2">Payment details</div>
        <div className="card-body">
          {showPay ? (
            <>
              <dl className="row mb-0 small ile-post-sale-dl gy-2 align-items-start">
                <dt className="col-sm-4 text-muted">Amount due</dt>
                <dd className="col-sm-8">{snapshot?.payment?.amountLabel || '—'}</dd>
                <dt className="col-sm-4 text-muted">Due by</dt>
                <dd className="col-sm-8">{formatDue(snapshot?.payment?.dueAt)}</dd>
                <dt className="col-sm-4 text-muted">Status</dt>
                <dd className="col-sm-8">
                  <span className="badge text-bg-warning text-dark">Awaiting payment</span>
                </dd>
              </dl>
              <p className="small text-muted mt-3 mb-4">
                Complete your first lease payment to generate your invoice. Choose a payment option — your gateway can be
                wired in later; we record the method you select.
              </p>

              {!showMethodPicker ? (
                <button
                  type="button"
                  className="btn btn-dark"
                  disabled={submitting}
                  onClick={() => {
                    setShowMethodPicker(true);
                  }}
                >
                  Complete payment
                </button>
              ) : (
                <>
                  <div className="border rounded bg-light p-3 mb-3">
                    <h2 className="h6 mb-3 text-dark">Select payment method</h2>
                    <div className="list-group list-group-flush rounded overflow-hidden border bg-white">
                      {PAYMENT_METHODS.map(({ id, label, hint, Icon }) => (
                        <label
                          key={id}
                          className={`list-group-item list-group-item-action d-flex align-items-start gap-3 py-3 mb-0 ile-payment-method-option ${
                            selectedMethod === id ? 'ile-payment-method-option--selected' : ''
                          }`}
                          style={{ cursor: 'pointer' }}
                        >
                          <input
                            type="radio"
                            className="form-check-input mt-1 flex-shrink-0"
                            name="ile-payment-method"
                            value={id}
                            checked={selectedMethod === id}
                            onChange={() => setSelectedMethod(id)}
                          />
                          <span className="text-primary flex-shrink-0 mt-1" aria-hidden>
                            <Icon size={20} />
                          </span>
                          <span className="flex-grow-1">
                            <span className="fw-semibold d-block text-body">{label}</span>
                            <span className="small text-muted">{hint}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <button
                      type="button"
                      className="btn btn-dark"
                      disabled={submitting || !selectedMethod}
                      onClick={() => void handlePay()}
                    >
                      {submitting ? 'Processing…' : 'Confirm & pay'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={submitting}
                      onClick={() => {
                        setShowMethodPicker(false);
                        setSelectedMethod('');
                      }}
                    >
                      Back
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <p className="text-muted mb-0 small">
              No payment is due at this stage.{' '}
              <Link to="/infrastructure-leasing/my-enquiries">Return to My enquiries</Link>
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 small">
        <Link to={`${basePath}/agreement`} className="text-muted">
          ← Back to agreement
        </Link>
      </div>
      </div>
    </div>
  );
}
